
import {computed, defineComponent, onMounted, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';

const emptyForm = () => ({
  id: 0,
  date: new Date().toISOString().slice(0, 10),
  categoryId: 0,
  vendorId: 0,
  amount: 0,
  taxPercent: 0,
  currency: 'PKR',
  paymentMethod: '',
  costCenter: '',
  isRecurring: false,
  recurrence: 'monthly',
  departmentId: 0,
  attachmentId: 0,
  attachmentFile: null as File | null,
  description: '',
});

export default defineComponent({
  name: 'CompanyExpenses',
  setup() {
    const baseUrl = (window as unknown as {appGlobal: {baseUrl: string}})
      .appGlobal.baseUrl;
    const expenseApi = new APIService(baseUrl, '/api/v2/finance/expenses');
    const categoryApi = new APIService(baseUrl, '/api/v2/finance/categories');
    const vendorApi = new APIService(baseUrl, '/api/v2/finance/vendors');
    const departmentApi = new APIService(
      baseUrl,
      '/api/v2/finance/departments',
    );
    const tab = ref('expenses');
    const expenses = ref<any[]>([]);
    const categories = ref<any[]>([]);
    const vendors = ref<any[]>([]);
    const departments = ref<any[]>([]);
    const page = ref(1);
    const pageSize = 50;
    const totalRecords = ref(0);
    const selectedIds = ref<number[]>([]);
    const pageCount = computed(() =>
      Math.max(1, Math.ceil(totalRecords.value / pageSize)),
    );
    const allSelected = computed(
      () =>
        expenses.value.length > 0 &&
        expenses.value.every((item) =>
          selectedIds.value.includes(Number(item.id)),
        ),
    );
    const loading = ref(false);
    const saving = ref(false);
    const savingMaster = ref(false);
    const showForm = ref(false);
    const form = ref(emptyForm());
    const newCategory = ref('');
    const newCategoryParentId = ref(0);
    const vendorForm = ref({
      name: '',
      contactPerson: '',
      phone: '',
      email: '',
      taxNo: '',
      address: '',
    });
    const errorMessage = ref('');
    const filters = ref({
      dateFrom: '',
      dateTo: '',
      departmentId: '',
      categoryId: '',
      vendorId: '',
      status: '',
      search: '',
    });
    const activeCategories = computed(() =>
      categories.value.filter((item) => Number(item.is_active) === 1),
    );
    const rootCategories = computed(() =>
      activeCategories.value.filter((item) => !item.parent_id),
    );
    const activeVendors = computed(() =>
      vendors.value.filter((item) => Number(item.is_active) === 1),
    );
    const totalAmount = computed(() =>
      (
        Number(form.value.amount || 0) *
        (1 + Number(form.value.taxPercent || 0) / 100)
      ).toFixed(2),
    );
    const pageTotal = computed(() => {
      const totals = expenses.value.reduce(
        (result: Record<string, number>, item) => {
          result[item.currency] =
            (result[item.currency] || 0) + Number(item.total || 0);
          return result;
        },
        {},
      );
      return Object.entries(totals)
        .map(([currency, total]) => `${currency} ${total.toFixed(2)}`)
        .join(' · ');
    });

    const loadMasters = async () => {
      const [categoryResponse, vendorResponse, departmentResponse] =
        await Promise.all([
          categoryApi.getAll(),
          vendorApi.getAll(),
          departmentApi.getAll(),
        ]);
      categories.value = categoryResponse.data.data || [];
      vendors.value = vendorResponse.data.data || [];
      departments.value = departmentResponse.data.data || [];
    };
    const loadExpenses = async () => {
      loading.value = true;
      errorMessage.value = '';
      try {
        const params: Record<string, string | number> = {
          limit: pageSize,
          offset: (page.value - 1) * pageSize,
        };
        Object.entries(filters.value).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const response = await expenseApi.getAll(params);
        expenses.value = response.data.data || [];
        totalRecords.value = Number(
          response.data.meta?.total ?? expenses.value.length,
        );
        selectedIds.value = [];
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          error?.data?.error?.message ||
          'Could not load expenses.';
      } finally {
        loading.value = false;
      }
    };
    const loadAll = async () => {
      try {
        await Promise.all([loadMasters(), loadExpenses()]);
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          error?.data?.error?.message ||
          'Could not load Finance data.';
      }
    };
    const resetFilters = () => {
      filters.value = {
        dateFrom: '',
        dateTo: '',
        departmentId: '',
        categoryId: '',
        vendorId: '',
        status: '',
        search: '',
      };
      page.value = 1;
      loadExpenses();
    };
    const searchExpenses = () => {
      page.value = 1;
      loadExpenses();
    };
    const changePage = (next: number) => {
      page.value = next;
      loadExpenses();
    };
    const toggleAll = (event: Event) => {
      const checked = (event.target as HTMLInputElement).checked;
      selectedIds.value = checked
        ? expenses.value.map((item) => Number(item.id))
        : [];
    };
    const bulkDelete = async () => {
      const ids = [...selectedIds.value];
      if (
        !ids.length ||
        !window.confirm(
          `Delete ${ids.length} selected expense(s)? Only draft or rejected records can be deleted.`,
        )
      )
        return;
      try {
        await Promise.all(ids.map((id) => expenseApi.delete(id)));
        await loadExpenses();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          'Some selected expenses could not be deleted.';
        await loadExpenses();
      }
    };
    const exportCsv = () => {
      const columns = [
        'Date',
        'Reference',
        'Category',
        'Vendor',
        'Amount',
        'Currency',
        'Status',
      ];
      const quote = (value: unknown) =>
        `"${String(value ?? '').replaceAll('"', '""')}"`;
      const rows = expenses.value.map((item) => [
        item.expense_date,
        item.ref_no,
        item.category_name,
        item.vendor_name,
        item.total,
        item.currency,
        item.status,
      ]);
      const blob = new Blob(
        [
          [columns, ...rows]
            .map((row) => row.map(quote).join(','))
            .join('\r\n'),
        ],
        {type: 'text/csv;charset=utf-8'},
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `company-expenses-page-${page.value}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };
    const startNewExpense = () => {
      form.value = emptyForm();
      showForm.value = true;
      tab.value = 'expenses';
    };
    const editExpense = (item: any) => {
      form.value = {
        id: Number(item.id),
        date: item.expense_date,
        categoryId: Number(item.category_id),
        vendorId: Number(item.vendor_id || 0),
        departmentId: Number(item.dept_id || 0),
        attachmentId: Number(item.attachment_id || 0),
        attachmentFile: null,
        amount: Number(item.amount),
        taxPercent: Number(item.tax_percent),
        currency: item.currency,
        paymentMethod: item.payment_method,
        costCenter: item.cost_center || '',
        isRecurring: Number(item.is_recurring) === 1,
        recurrence: item.recurrence || 'monthly',
        description: item.description || '',
      };
      showForm.value = true;
    };
    const closeForm = () => {
      showForm.value = false;
    };
    const saveExpense = async () => {
      saving.value = true;
      errorMessage.value = '';
      const payload = {
        date: form.value.date,
        categoryId: form.value.categoryId,
        vendorId: form.value.vendorId || null,
        departmentId: form.value.departmentId || null,
        amount: form.value.amount,
        taxPercent: form.value.taxPercent,
        currency: form.value.currency,
        paymentMethod: form.value.paymentMethod,
        costCenter: form.value.costCenter,
        isRecurring: form.value.isRecurring,
        recurrence: form.value.isRecurring ? form.value.recurrence : null,
        description: form.value.description,
      };
      try {
        const result = form.value.id
          ? await expenseApi.update(form.value.id, payload)
          : await expenseApi.create(payload);
        const expenseId = Number(result.data.data?.id || form.value.id);
        if (form.value.attachmentFile && expenseId) {
          const upload = new FormData();
          upload.append('attachment', form.value.attachmentFile);
          await expenseApi.request({
            method: 'POST',
            url: `/api/v2/finance/expenses/${expenseId}/attachment`,
            data: upload,
            headers: {'Content-Type': 'multipart/form-data'},
          });
        }
        showForm.value = false;
        await loadExpenses();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          'Could not save this expense.';
      } finally {
        saving.value = false;
      }
    };
    const selectAttachment = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0] || null;
      if (!file) return;
      if (
        file.size > 5 * 1024 * 1024 ||
        !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)
      ) {
        errorMessage.value =
          'Choose a PDF, JPEG, or PNG receipt no larger than 5 MB.';
        input.value = '';
        form.value.attachmentFile = null;
        return;
      }
      errorMessage.value = '';
      form.value.attachmentFile = file;
    };
    const transition = async (item: any, status: string) => {
      const approvalComment =
        status === 'rejected'
          ? window.prompt('Enter a reason for rejection:')
          : '';
      if (status === 'rejected' && !approvalComment?.trim()) return;
      try {
        await expenseApi.update(Number(item.id), {status, approvalComment});
        await loadExpenses();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          'Could not update expense status.';
      }
    };
    const deleteExpense = async (item: any) => {
      if (!window.confirm(`Delete expense ${item.ref_no}?`)) return;
      try {
        await expenseApi.delete(Number(item.id));
        await loadExpenses();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          'Could not delete this expense.';
      }
    };
    const createCategory = async () => {
      savingMaster.value = true;
      try {
        await categoryApi.create({
          name: newCategory.value,
          parentId: newCategoryParentId.value || null,
        });
        newCategory.value = '';
        newCategoryParentId.value = 0;
        await loadMasters();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not add category.';
      } finally {
        savingMaster.value = false;
      }
    };
    const setCategoryActive = async (item: any, active: boolean) => {
      try {
        await categoryApi.update(Number(item.id), {
          name: item.name,
          parentId: item.parent_id,
          isActive: active,
        });
        await loadMasters();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not update category.';
      }
    };
    const createVendor = async () => {
      savingMaster.value = true;
      try {
        await vendorApi.create(vendorForm.value);
        vendorForm.value = {
          name: '',
          contactPerson: '',
          phone: '',
          email: '',
          taxNo: '',
          address: '',
        };
        await loadMasters();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not add vendor.';
      } finally {
        savingMaster.value = false;
      }
    };
    const setVendorActive = async (item: any, active: boolean) => {
      try {
        await vendorApi.update(Number(item.id), {
          name: item.name,
          contactPerson: item.contact_person,
          phone: item.phone,
          email: item.email,
          taxNo: item.tax_no,
          address: item.address,
          isActive: active,
        });
        await loadMasters();
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message || 'Could not update vendor.';
      }
    };
    const statusLabel = (status: string) =>
      (({pending_approval: 'Pending Approval'} as Record<string, string>)[
        status
      ] || status.charAt(0).toUpperCase() + status.slice(1));
    onMounted(loadAll);
    return {
      tab,
      expenses,
      categories,
      vendors,
      departments,
      page,
      pageCount,
      totalRecords,
      selectedIds,
      allSelected,
      activeCategories,
      rootCategories,
      newCategoryParentId,
      activeVendors,
      loading,
      saving,
      savingMaster,
      showForm,
      form,
      newCategory,
      vendorForm,
      errorMessage,
      filters,
      totalAmount,
      pageTotal,
      loadExpenses,
      resetFilters,
      searchExpenses,
      changePage,
      toggleAll,
      bulkDelete,
      exportCsv,
      startNewExpense,
      editExpense,
      closeForm,
      saveExpense,
      selectAttachment,
      baseUrl,
      transition,
      deleteExpense,
      createCategory,
      setCategoryActive,
      createVendor,
      setVendorActive,
      statusLabel,
    };
  },
});
